import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	DEFAULT_DATE_TIME_FORMAT,
	FormattedDateTime,
	IDataListColumn,
	Id,
	useHasPermission,
} from "@dgs/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { EventIndexResource } from "../../../../shared/types/event";
import { PermissionName } from "../../../../shared/types/permission";

interface Props {
	onDelete?: (eventId: Id) => Promise<boolean>;
	onDetachEvent?: (eventId: Id) => Promise<void>;
	showPreviewButton?: boolean;
	hasParticipantStatusColumn?: boolean;
}

export const useEventListColumns = ({
	onDelete,
	onDetachEvent,
	showPreviewButton,
	hasParticipantStatusColumn,
}: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const canCreate = useHasPermission<PermissionName>(["event.create"]);
	const canUpdate = useHasPermission<PermissionName>(["event.update"]);
	const canDelete = useHasPermission<PermissionName>(["event.delete"]);

	const eventListValueColumns: IDataListColumn<EventIndexResource>[] = useMemo(() => {
		const columns: IDataListColumn<EventIndexResource>[] = [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Start"),
				valueKey: "start",
				type: "value",
				render: (value) => (
					<FormattedDateTime
						dateTime={value.start}
						format={{ ...DEFAULT_DATE_TIME_FORMAT, timeZone: value.timezone }}
					/>
				),
			},
			{
				heading: t("End"),
				valueKey: "end",
				type: "value",
				render: (value) => (
					<FormattedDateTime dateTime={value.end} format={{ ...DEFAULT_DATE_TIME_FORMAT, timeZone: value.timezone }} />
				),
			},
			{
				heading: t("Project managers"),
				valueKey: "projectManagers",
				type: "value",
				render: (value) => (
					<>
						{value.projectManagers.length > 0
							? value.projectManagers.map((value) => `${value.name} (${value.email})`).join(", ")
							: "-"}
					</>
				),
			},
			{
				heading: t("Highlight"),
				valueKey: "isHighlight",
				type: "value",
				render: (value) => (value.isHighlight ? t("Yes") : t("No")),
			},
			{
				heading: t("Visibility"),
				valueKey: "visibility",
				type: "value",
				render: (value) => t(`eventVisibility.${value.visibility}`),
			},
		];
		if (hasParticipantStatusColumn) {
			columns.push({
				heading: t("Participation status"),
				valueKey: "participationStatus",
				type: "value",
				render: ({ participationStatus }) =>
					participationStatus === "none" ? "-" : t(`eventParticipationStatus.${participationStatus}`),
			});
		}
		return columns;
	}, [t, hasParticipantStatusColumn]);

	const eventListColumns: IDataListColumn<EventIndexResource>[] = useMemo(() => {
		const eventListActionColumn: IDataListColumn<EventIndexResource>[] =
			canCreate || canUpdate || onDelete || onDetachEvent
				? [
						{
							heading: t("Actions"),
							type: "actions",
							size: "max-content",
							action: ({ id, hasProjectMemberPermissions, hasProjectManagerPermissions }) => (
								<ContextMenu hideIconOnOnlyShortcuts={true} displayShortcutsAs="icon">
									{!showPreviewButton && (canUpdate || hasProjectMemberPermissions) && (
										<ContextMenuItem
											action={() => navigate(`${id}`)}
											title={t("Edit event")}
											label={t("Edit event")}
											icon="edit"
											shortcut="primary"
										/>
									)}
									{showPreviewButton && (
										<ContextMenuItem
											action={() => navigate(`/events/active/${id}/details`)}
											title={t("To event")}
											label={t("To event")}
											icon="eye"
											shortcut="primary"
										/>
									)}
									{canCreate && (
										<ContextMenuItem
											action={() => navigate(`/events/create/from-event/${id}/base-data`)}
											title={t("Duplicate event")}
											label={t("Duplicate event")}
											icon="copy"
										/>
									)}
									{(canDelete || hasProjectManagerPermissions) && onDelete && (
										<ContextMenuDeleteItem
											action={() => onDelete(id)}
											title={t("Archive event")}
											label={t("Archive event")}
											heading={t("Archive event")}
											labels={{ close: t("Close"), confirm: t("Confirm") }}
										>
											{t("Are you sure you want to archive this event?")}
										</ContextMenuDeleteItem>
									)}
									{(canUpdate || hasProjectManagerPermissions) && onDetachEvent && (
										<ContextMenuDeleteItem
											action={() => onDetachEvent(id)}
											label={t("Detach event")}
											heading={t("Detach event")}
											labels={{ close: t("Close"), confirm: t("Confirm") }}
											title={t("Detach event")}
											shortcut={true}
										>
											{t(`Are you sure you want to remove this event from the user?`)}
										</ContextMenuDeleteItem>
									)}
								</ContextMenu>
							),
						},
					]
				: [];

		return [...eventListValueColumns, ...eventListActionColumn];
	}, [canCreate, canDelete, canUpdate, eventListValueColumns, navigate, onDelete, onDetachEvent, showPreviewButton, t]);

	return {
		eventListValueColumns,
		eventListColumns,
	};
};
